/**
 * Running order for multi-artist shows (takeovers).
 *
 * A takeover is archived as ONE recording of the whole show. The running order
 * marks where each set starts inside that recording, so the website can play
 * from a DJ's start, after-hours can play a single set, and each artist gets a
 * link to their own set — without uploading every set separately.
 *
 * Stored at `happenings.metadata.sets`, ordered by `start_seconds`. A set ends
 * where the next one starts; the last one runs to the end of the recording.
 */

export type ShowSet = {
    /** Offset into the full-show recording where this set starts. */
    start_seconds: number;
    /**
     * Artists playing this set (`people.id`). Several = b2b. Empty = a segment
     * that is not one artist's set, e.g. the collective's intro; give it a label.
     */
    person_ids: string[];
    /** Display name override. Falls back to the artists' names joined with "b2b". */
    label?: string | null;
    /**
     * Optional separate upload of just this set. When set, consumers prefer it
     * over seeking into the full recording.
     */
    url?: string | null;
};

type NamedArtist = { person_id: string; name: string | null; slug?: string | null };

/** Parse "1:02:03", "58:30", "1h02m03s", "62m", "90s" or a bare number of minutes into seconds. */
export function parseTimecode(input: string): number | null {
    const s = input.trim().toLowerCase();
    if (!s) return null;

    const colon = s.match(/^(\d+)(?::(\d{1,2}))(?::(\d{1,2}))?$/);
    if (colon) {
        const parts = [colon[1], colon[2], colon[3]].filter((p): p is string => p !== undefined).map(Number);
        if (parts.slice(1).some((p) => p >= 60)) return null;
        return parts.length === 3
            ? parts[0] * 3600 + parts[1] * 60 + parts[2]
            : parts[0] * 60 + parts[1];
    }

    const units = s.match(/^(?:(\d+)h)?\s*(?:(\d+)m(?:in)?)?\s*(?:(\d+)s)?$/);
    if (units && (units[1] || units[2] || units[3])) {
        return Number(units[1] ?? 0) * 3600 + Number(units[2] ?? 0) * 60 + Number(units[3] ?? 0);
    }

    if (/^\d+$/.test(s)) return Number(s) * 60;
    return null;
}

/** Format seconds as "H:MM:SS" — the form SoundCloud turns into clickable timestamps. */
export function formatTimecode(totalSeconds: number): string {
    const t = Math.max(0, Math.floor(totalSeconds));
    const h = Math.floor(t / 3600);
    const m = Math.floor((t % 3600) / 60);
    const sec = t % 60;
    return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

/** Validate and normalise whatever is stored at `metadata.sets`. */
export function sanitizeShowSets(raw: unknown): ShowSet[] | undefined {
    if (!Array.isArray(raw)) return undefined;
    return raw
        .filter((s): s is Record<string, unknown> => typeof s === 'object' && s !== null)
        .filter((s) => typeof s.start_seconds === 'number' && Number.isFinite(s.start_seconds) && s.start_seconds >= 0)
        .map((s) => {
            const personIds = Array.isArray(s.person_ids)
                ? Array.from(new Set(s.person_ids.filter((id): id is string => typeof id === 'string' && id.length > 0)))
                : [];
            const label = typeof s.label === 'string' && s.label.trim() ? s.label.trim() : null;
            const url = typeof s.url === 'string' && s.url.trim() ? s.url.trim() : null;
            return { start_seconds: Math.floor(s.start_seconds as number), person_ids: personIds, label, url };
        })
        // A set with neither artists nor a label has nothing to show.
        .filter((s) => s.person_ids.length > 0 || s.label)
        .sort((a, b) => a.start_seconds - b.start_seconds);
}

/** The name shown for a set: its label, else its artists' names joined with "b2b". */
export function showSetName(set: ShowSet, artists: NamedArtist[]): string {
    if (set.label) return set.label;
    const byId = new Map(artists.map((a) => [a.person_id, a]));
    const names = set.person_ids
        .map((id) => byId.get(id)?.name)
        .filter((n): n is string => !!n);
    return names.length > 0 ? names.join(' b2b ') : 'Unknown artist';
}

/** Sets with their end offset filled in from the next set's start (null for the last one). */
export function withSetEnds(sets: ShowSet[]): Array<ShowSet & { end_seconds: number | null }> {
    const sorted = [...sets].sort((a, b) => a.start_seconds - b.start_seconds);
    return sorted.map((set, i) => ({ ...set, end_seconds: sorted[i + 1]?.start_seconds ?? null }));
}

/** The first set an artist plays in, if any. */
export function findSetForPerson(sets: ShowSet[] | undefined, personId: string): ShowSet | undefined {
    return (sets ?? []).find((s) => s.person_ids.includes(personId));
}

const normaliseName = (name: string) =>
    name
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '');

// "A b2b B", "A & B", "A + B", "A x B", "A, B" all mean several artists on one set.
const ARTIST_SEPARATOR = /\s+(?:b2b|b3b|&|\+|x|and)\s+|\s*,\s*/i;
// A timecode at the start of the line, then an optional dash/colon/pipe separator.
const LINE = /^\s*[[(]?(\d+(?::\d{1,2}){1,2}|\d+h(?:\d+m)?(?:\d+s)?|\d+m(?:\d+s)?)[\])]?\s*(?:[-–—:|.]\s*)?(.+?)\s*$/i;

export type ParsedRunningOrder = {
    sets: ShowSet[];
    /** Lines that had no recognisable timecode. */
    skipped: string[];
    /** Names that matched no artist on the show; their sets keep the text as a label. */
    unmatched: string[];
};

/**
 * Parse pasted running-order text such as a SoundCloud comment or description:
 *
 *     0:00 Collective intro
 *     0:58:30 - DJ One
 *     1:45:00 DJ Two b2b DJ Three
 *
 * Names are matched against the show's artists ignoring case, accents and
 * punctuation. A line whose names all match becomes an artist set; otherwise
 * the text is kept as the set's label, alongside whichever artists did match.
 */
export function parseRunningOrder(text: string, artists: NamedArtist[]): ParsedRunningOrder {
    const byName = new Map<string, string>();
    for (const a of artists) {
        if (a.name) byName.set(normaliseName(a.name), a.person_id);
        if (a.slug) byName.set(normaliseName(a.slug), a.person_id);
    }

    const sets: ShowSet[] = [];
    const skipped: string[] = [];
    const unmatched: string[] = [];

    for (const rawLine of text.split('\n')) {
        const line = rawLine.trim();
        if (!line) continue;
        const m = line.match(LINE);
        const start = m ? parseTimecode(m[1]) : null;
        if (!m || start === null) {
            skipped.push(line);
            continue;
        }
        const rest = m[2].trim();
        const exact = byName.get(normaliseName(rest));
        const parts = rest.split(ARTIST_SEPARATOR).map((p) => p.trim()).filter(Boolean);
        const matched = exact ? [exact] : parts.map((p) => byName.get(normaliseName(p))).filter((id): id is string => !!id);
        const allMatched = !!exact || (parts.length > 0 && matched.length === parts.length);
        if (!allMatched) unmatched.push(rest);
        sets.push({
            start_seconds: start,
            person_ids: Array.from(new Set(matched)),
            label: allMatched ? null : rest,
            url: null,
        });
    }

    return { sets: sanitizeShowSets(sets) ?? [], skipped, unmatched };
}

/** Running order as text to paste into a SoundCloud description or comment. */
export function buildRunningOrderText(sets: ShowSet[], artists: NamedArtist[]): string {
    return withSetEnds(sets)
        .map((s) => `${formatTimecode(s.start_seconds)} ${showSetName(s, artists)}`)
        .join('\n');
}

/** A SoundCloud track link that starts playback at `seconds`. */
export function soundCloudUrlAt(url: string, seconds: number): string {
    const base = url.split('#')[0];
    return seconds > 0 ? `${base}#t=${formatTimecode(seconds)}` : base;
}

/** A show page link that opens on one artist's set, e.g. `/shows/takeover?set=dj-one`. */
export function showPageUrlForSet(showPageUrl: string, artistSlug: string): string {
    const sep = showPageUrl.includes('?') ? '&' : '?';
    return `${showPageUrl}${sep}set=${encodeURIComponent(artistSlug)}`;
}
